import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeScannerState, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { useStyletron } from 'baseui';

const SCANNER_ELEMENT_ID = 'apt300-scanner2-reader';
const SELECTED_EVENT_STORAGE_KEY = 'scanner2SelectedProductId';
const DUPLICATE_COOLDOWN_MS = 1500;
const SUCCESS_RESUME_DELAY_MS = 1200;
const ERROR_RESUME_DELAY_MS = 2500;
const READER_ASPECT_RATIO = 3 / 4;

const ScannerPage2 = ({ authPassword }) => {
    const [css] = useStyletron();
    const scannerRef = useRef(null);
    const submitBarcodeRef = useRef(null);
    const mountedRef = useRef(true);
    const submittingRef = useRef(false);
    const cooldownRef = useRef(new Map());
    const resumeTimerRef = useRef(null);

    const [manualCode, setManualCode] = useState('');
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('neutral');
    const [customer, setCustomer] = useState(null);
    const [lastCode, setLastCode] = useState('');
    const [events, setEvents] = useState([]);
    const [selectedProductId, setSelectedProductId] = useState('');
    const [admittedCount, setAdmittedCount] = useState(0);
    const [scannerStatus, setScannerStatus] = useState('starting');
    const [cameraError, setCameraError] = useState('');

    const clearResumeTimer = () => {
        if (resumeTimerRef.current) {
            clearTimeout(resumeTimerRef.current);
            resumeTimerRef.current = null;
        }
    };

    const vibrate = (pattern) => {
        if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
            navigator.vibrate(pattern);
        }
    };

    const resumeScanner = useCallback(() => {
        clearResumeTimer();

        const scanner = scannerRef.current;
        if (!scanner) {
            return;
        }

        try {
            scanner.resume();
            setScannerStatus('ready');
        } catch (error) {
            // Some browsers throw if resume is called before a pause has settled.
            setScannerStatus('ready');
        }
    }, []);

    const pauseScanner = useCallback(() => {
        const scanner = scannerRef.current;
        if (!scanner) {
            return;
        }

        try {
            scanner.pause(true);
        } catch (error) {
            // Ignore pause failures; the fallback is still to block duplicate submissions in JS.
        }
    }, []);

    const eventOptions = useMemo(() => (
        events.map((event) => ({
            value: event.id,
            label: [event.title, event.date, event.venue].filter(Boolean).join(' | '),
        }))
    ), [events]);

    const loadEvents = useCallback(async () => {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/storefront/products`);
        if (!response.ok) {
            throw new Error('Failed to load events.');
        }

        const data = await response.json();
        const nextEvents = Array.isArray(data) ? data : [];
        setEvents(nextEvents);

        const params = new URLSearchParams(window.location.search);
        const requestedProductId = params.get('event');
        const storedProductId = localStorage.getItem(SELECTED_EVENT_STORAGE_KEY);
        const preferredProductId = [requestedProductId, storedProductId].find((value) => value && nextEvents.some((event) => event.id === value));
        const fallbackProductId = nextEvents[0]?.id || '';
        setSelectedProductId((current) => {
            const currentIsValid = current && nextEvents.some((event) => event.id === current);
            const nextProductId = currentIsValid ? current : (preferredProductId || fallbackProductId);
            if (nextProductId) {
                localStorage.setItem(SELECTED_EVENT_STORAGE_KEY, nextProductId);
            }
            return nextProductId;
        });
    }, []);

    const loadScanCount = useCallback(async () => {
        if (!authPassword || !selectedProductId) {
            setAdmittedCount(0);
            return;
        }

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/admin/scan-stats?productId=${encodeURIComponent(selectedProductId)}`, {
            headers: {
                'x-admin-password': authPassword,
            },
        });

        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(data.error || 'Failed to load admitted count.');
        }

        const data = await response.json();
        const nextCount = Number(data.successfulScans);
        if (Number.isFinite(nextCount)) {
            setAdmittedCount((current) => Math.max(current, nextCount));
        }
    }, [authPassword, selectedProductId]);

    const submitBarcode = useCallback(async (barcode) => {
        const normalizedCode = String(barcode || '').trim();
        if (!normalizedCode || submittingRef.current) {
            return;
        }

        const now = Date.now();
        const lastSeenAt = cooldownRef.current.get(normalizedCode) || 0;
        if (now - lastSeenAt < DUPLICATE_COOLDOWN_MS) {
            return;
        }

        cooldownRef.current.set(normalizedCode, now);
        submittingRef.current = true;
        setLastCode(normalizedCode);
        setManualCode(normalizedCode);
        setCustomer(null);
        setMessage('Validating ticket...');
        setMessageType('neutral');
        setScannerStatus('processing');
        pauseScanner();

        let nextResumeDelay = SUCCESS_RESUME_DELAY_MS;

        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/scan`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ barcode: normalizedCode }),
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                if (response.status >= 500) {
                    throw new Error('Server error while validating. Try again.');
                }

                throw new Error(data.error || 'Scan failed');
            }

            setMessage(data.message || 'Ticket accepted');
            setMessageType('success');
            setCustomer(data.customer || null);
            loadScanCount().catch(() => undefined);
            vibrate(60);
        } catch (error) {
            nextResumeDelay = ERROR_RESUME_DELAY_MS;

            const errorMessage = error?.message || 'Scan failed';
            const isNetworkError = error?.name === 'TypeError' || /failed to fetch/i.test(errorMessage);

            if (isNetworkError) {
                setMessage(
                    typeof navigator !== 'undefined' && navigator.onLine === false
                        ? 'No internet connection. Reconnect and try again.'
                        : 'Network error. Check connection and try again.'
                );
            } else {
                setMessage(errorMessage);
            }
            setMessageType('error');
            setCustomer(null);
            vibrate([60, 60, 60]);
        } finally {
            submittingRef.current = false;
            clearResumeTimer();
            resumeTimerRef.current = setTimeout(() => {
                if (!mountedRef.current) {
                    return;
                }
                setMessage('');
                setMessageType('neutral');
                resumeScanner();
            }, nextResumeDelay);
        }
    }, [loadScanCount, pauseScanner, resumeScanner]);

    useEffect(() => {
        submitBarcodeRef.current = submitBarcode;
    }, [submitBarcode]);

    useEffect(() => {
        loadEvents().catch((error) => {
            setMessage(error.message || 'Failed to load events.');
            setMessageType('error');
        });
    }, [loadEvents]);

    useEffect(() => {
        loadScanCount().catch(() => undefined);
    }, [loadScanCount]);

    useEffect(() => {
        if (!selectedProductId) {
            return;
        }

        localStorage.setItem(SELECTED_EVENT_STORAGE_KEY, selectedProductId);
        setAdmittedCount(0);
    }, [selectedProductId]);

    const handleEventChange = (event) => {
        setSelectedProductId(event.target.value);
    };

    useEffect(() => {
        mountedRef.current = true;
        let cancelled = false;

        const startScanner = async () => {
            setScannerStatus('starting');
            setCameraError('');

            if (!Html5Qrcode || !Html5QrcodeSupportedFormats) {
                setCameraError('Scanner library failed to load.');
                setScannerStatus('error');
                return;
            }

            const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID, {
                formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
                verbose: false,
            });
            scannerRef.current = scanner;

            try {
                await scanner.start(
                    { facingMode: 'environment' },
                    {
                        aspectRatio: READER_ASPECT_RATIO,
                        fps: 12,
                        disableFlip: false,
                        qrbox: (viewfinderWidth, viewfinderHeight) => {
                            const edge = Math.min(viewfinderWidth, viewfinderHeight);
                            return {
                                width: Math.max(220, Math.floor(edge * 0.78)),
                                height: Math.max(220, Math.floor(edge * 0.78)),
                            };
                        },
                        rememberLastUsedCamera: true,
                        experimentalFeatures: {
                            useBarCodeDetectorIfSupported: true,
                        },
                    },
                    (decodedText) => {
                        if (!cancelled && submitBarcodeRef.current) {
                            submitBarcodeRef.current(decodedText);
                        }
                    },
                    () => {
                        // Decode misses are expected; keep the loop quiet.
                    }
                );

                if (!cancelled) {
                    setScannerStatus('ready');
                }
            } catch (error) {
                const message = error?.message || 'Unable to start the camera scanner.';
                if (!cancelled) {
                    setCameraError(message);
                    setScannerStatus('error');
                }
            }
        };

        startScanner();

        return () => {
            cancelled = true;
            mountedRef.current = false;
            clearResumeTimer();

            const scanner = scannerRef.current;
            scannerRef.current = null;
            if (scanner) {
                let stopPromise = Promise.resolve();

                try {
                    const state = typeof scanner.getState === 'function' ? scanner.getState() : null;
                    const canStop = state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED;

                    if (canStop) {
                        stopPromise = scanner.stop().catch(() => undefined);
                    }
                } catch (error) {
                    stopPromise = Promise.resolve();
                }

                stopPromise.finally(() => {
                    try {
                        const clearResult = scanner.clear();
                        if (clearResult && typeof clearResult.catch === 'function') {
                            clearResult.catch(() => undefined);
                        }
                    } catch (error) {
                        // Ignore clear failures during teardown.
                    }
                });
            }
        };
    }, []);

    const handleManualSubmit = async (event) => {
        event.preventDefault();
        await submitBarcode(manualCode);
    };

    const handleReset = () => {
        setManualCode('');
        setCustomer(null);
        setLastCode('');
        setMessage('');
        setMessageType('neutral');
        resumeScanner();
    };

    const messageColor = useMemo(() => {
        if (messageType === 'success') return '#027a48';
        if (messageType === 'error') return '#b42318';
        return '#2b170f';
    }, [messageType]);

    const outerContainerStyle = css({
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #d23c1b 0%, #BA5A31 48.55%, #E59F71 100%)',
        padding: '1rem',
        display: 'flex',
        justifyContent: 'center',
        overflowX: 'hidden',
        boxSizing: 'border-box',
    });

    const containerStyle = css({
        width: '100%',
        maxWidth: '560px',
        padding: '1rem',
        boxSizing: 'border-box',
        background: '#fff',
        borderRadius: '14px',
        color: '#333',
        boxShadow: '0 12px 30px rgba(0,0,0,0.18)',
        alignSelf: 'center',
        overflowX: 'hidden',
    });

    const readerShellStyle = css({
        position: 'relative',
        borderRadius: '14px',
        overflow: 'hidden',
        background: '#111',
        minHeight: '360px',
        aspectRatio: '3 / 4',
        maxHeight: '70vh',
        marginBottom: '1rem',
        width: '100%',
    });

    const scanFrameStyle = css({
        position: 'absolute',
        inset: '50% auto auto 50%',
        width: '72%',
        maxWidth: '320px',
        aspectRatio: '1 / 1',
        transform: 'translate(-50%, -50%)',
        border: '3px solid rgba(255,255,255,0.9)',
        borderRadius: '18px',
        boxShadow: '0 0 0 9999px rgba(0,0,0,0.22)',
        pointerEvents: 'none',
    });

    const inputStyle = {
        width: '100%',
        padding: '0.85rem',
        fontSize: '1rem',
        borderRadius: '8px',
        border: '1px solid #ccc',
        boxSizing: 'border-box',
    };

    const buttonStyle = {
        padding: '0.85rem 1.1rem',
        fontSize: '1rem',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: '#d23c1b',
        color: '#fff',
        cursor: 'pointer',
    };

    const secondaryButtonStyle = {
        ...buttonStyle,
        backgroundColor: '#6b301d',
    };

    return (
        <div className={outerContainerStyle}>
            <div className={containerStyle}>
                <style>{`
                    #${SCANNER_ELEMENT_ID} {
                        width: 100%;
                        height: 100%;
                        min-height: 360px;
                        background: #111;
                    }
                    #${SCANNER_ELEMENT_ID} > div,
                    #${SCANNER_ELEMENT_ID}__scan_region {
                        width: 100% !important;
                        height: 100% !important;
                        min-height: 360px;
                    }
                    #${SCANNER_ELEMENT_ID} video {
                        width: 100% !important;
                        height: 100% !important;
                        object-fit: cover;
                        border-radius: 14px;
                        display: block;
                    }
                    #${SCANNER_ELEMENT_ID}__dashboard_section,
                    #${SCANNER_ELEMENT_ID}__dashboard,
                    #${SCANNER_ELEMENT_ID}__header_message {
                        display: none !important;
                    }
                    #${SCANNER_ELEMENT_ID} img {
                        display: none !important;
                    }
                `}</style>

                <h1 style={{ fontFamily: 'Impact, sans-serif', fontSize: '2.4rem', margin: '0 0 0.35rem' }}>Scanner 2</h1>

                <div style={{ marginBottom: '0.9rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 700, color: '#2b170f' }}>Event</label>
                    <select value={selectedProductId} onChange={handleEventChange} style={inputStyle}>
                        {eventOptions.length === 0 ? <option value="">Loading events...</option> : null}
                        {eventOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>

                <div style={{ marginBottom: '0.9rem', padding: '0.8rem 0.9rem', borderRadius: '10px', background: '#f6f1ed' }}>
                    <div style={{ fontSize: '0.8rem', letterSpacing: '0.04em', textTransform: 'uppercase', color: '#6b5b53' }}>Successful admits</div>
                    <div style={{ fontSize: '2rem', fontWeight: 800, color: '#2b170f', lineHeight: 1.1 }}>{admittedCount}</div>
                </div>

                <div style={{ marginBottom: '0.75rem', fontWeight: 700, color: scannerStatus === 'processing' ? '#b54708' : '#2b170f' }}>
                    {scannerStatus === 'starting' ? 'Starting camera...' : null}
                    {scannerStatus === 'ready' ? 'Scanner ready' : null}
                    {scannerStatus === 'processing' ? 'Processing scan...' : null}
                    {scannerStatus === 'error' ? 'Camera unavailable' : null}
                </div>

                <div className={readerShellStyle}>
                    <div id={SCANNER_ELEMENT_ID} />
                    {scannerStatus !== 'processing' ? <div className={scanFrameStyle} /> : null}
                </div>

                {cameraError ? (
                    <p style={{ color: '#b42318', marginTop: 0 }}>
                        {cameraError}
                    </p>
                ) : null}

                {message ? (
                    <p style={{ color: messageColor, fontWeight: 700, fontSize: '1.05rem' }}>{message}</p>
                ) : null}

                {customer ? (
                    <div style={{ marginBottom: '1rem', padding: '0.9rem', borderRadius: '10px', background: '#f6f8f7' }}>
                        <div style={{ fontWeight: 700 }}>Last Accepted</div>
                        <div style={{ overflowWrap: 'anywhere' }}>{customer.name || 'No name'}</div>
                        <div style={{ overflowWrap: 'anywhere' }}>{customer.email || 'No email'}</div>
                        {lastCode ? <div style={{ marginTop: '0.25rem', color: '#6b5b53', fontSize: '0.92rem', overflowWrap: 'anywhere' }}>{lastCode}</div> : null}
                    </div>
                ) : null}

                <form onSubmit={handleManualSubmit}>
                    <input
                        type="text"
                        value={manualCode}
                        onChange={(event) => setManualCode(event.target.value)}
                        placeholder="Paste or type a barcode"
                        style={inputStyle}
                    />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.75rem' }}>
                        <button type="submit" style={{ ...buttonStyle, flex: '1 1 180px' }}>Submit Code</button>
                        <button type="button" style={{ ...secondaryButtonStyle, flex: '1 1 140px' }} onClick={handleReset}>Reset</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ScannerPage2;